import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  TrendingUp, 
  TrendingDown, 
  FileText, 
  Receipt, 
  Wallet,
  ArrowUpRight,
  Plus,
  Calendar,
  Users
} from "lucide-react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import axios from "axios";

const Dashboard = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, [user?.active_company_id]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("sv-SE");
  };

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "Maj", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"];
  
  const chartData = summary?.monthly_data?.map(d => ({
    month: monthNames[parseInt(d._id?.substring(5)) - 1] || d._id?.substring(5),
    total: d.total || 0,
    count: d.count || 0
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user?.active_company_id) {
    return (
      <div className="text-center py-16" data-testid="no-company-message">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <FileText className="text-slate-400" size={32} />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">{t('company.selectCompany')}</h2>
        <p className="text-slate-500 mb-6">{t('company.createCompany')}</p>
        <Link to="/settings">
          <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-company-btn">
            <Plus size={16} className="mr-2" />
            {t('company.addCompany')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('nav.dashboard')}</h1>
          <p className="text-slate-500 mt-1">{t('dashboard.welcome')}, {user?.name}</p>
        </div>
        <div className="flex gap-3">
          <Link to="/bookkeeping">
            <Button variant="outline" data-testid="new-entry-btn">
              <Plus size={16} className="mr-2" />
              {t('dashboard.newJournalEntry')}
            </Button>
          </Link>
          <Link to="/invoices">
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="scan-invoice-btn">
              <Receipt size={16} className="mr-2" />
              {t('dashboard.scanInvoice')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-base" data-testid="stat-invoices">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center">
                <FileText className="text-indigo-600" size={24} />
              </div>
              <span className="text-sm text-slate-500">{t('dashboard.invoices')}</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 display-amount">
              {summary?.invoices?.total_invoices || 0}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {summary?.invoices?.paid || 0} {t('dashboard.paid')}, {summary?.invoices?.sent || 0} {t('dashboard.sent')}
            </p>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-revenue">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                <TrendingUp className="text-green-600" size={24} />
              </div>
              <span className="text-sm text-slate-500">{t('dashboard.revenue')}</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 font-display">
              {formatCurrency(summary?.invoices?.total_revenue)}
            </p>
            <p className="text-sm text-slate-500 mt-1">{t('dashboard.totalInvoiced')}</p>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-unpaid">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                <Receipt className="text-amber-600" size={24} />
              </div>
              <span className="text-sm text-slate-500">{t('dashboard.unpaid')}</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 display-amount">
              {formatCurrency(summary?.invoices?.unpaid_amount)}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {(summary?.invoices?.sent || 0) + (summary?.invoices?.overdue || 0)} {t('dashboard.invoice')}
            </p>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-customers">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                <Users className="text-purple-600" size={24} />
              </div>
              <span className="text-sm text-slate-500">{t('dashboard.customers')}</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 display-amount">
              {summary?.customer_count || 0}
            </p>
            <p className="text-sm text-slate-500 mt-1">{t('dashboard.registered')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart and Recent Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <Card className="card-base lg:col-span-2" data-testid="monthly-chart">
          <CardHeader>
            <CardTitle className="text-lg">{t('dashboard.invoicingPerMonth')}</CardTitle>
            <CardDescription>{t('dashboard.totalInvoicedPerMonth')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => `${v/1000}k`} />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value), name === "total" ? "Belopp" : "Antal"]}
                      contentStyle={{ 
                        background: "white", 
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px"
                      }}
                    />
                    <Bar dataKey="total" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  Inga fakturor att visa
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Invoices */}
        <Card className="card-base" data-testid="recent-invoices">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{t('dashboard.recentInvoices')}</CardTitle>
              <Link to="/sales-invoices" className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                {t('dashboard.viewAll')} <ArrowUpRight size={14} />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {summary?.recent_invoices?.length > 0 ? (
                summary.recent_invoices.map((invoice) => {
                  // Format invoice number: F00003 -> F3
                  const formattedNumber = invoice.invoice_number || "-";
                  return (
                    <div key={invoice.id} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <Link 
                          to={`/sales-invoices?edit=${invoice.id}`}
                          className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center hover:bg-indigo-200 transition-colors cursor-pointer"
                          data-testid={`invoice-link-${invoice.id}`}
                        >
                          <span className="text-xs font-medium text-indigo-600">{formattedNumber}</span>
                        </Link>
                        <div>
                          <p className="text-sm font-medium text-slate-900 truncate max-w-[150px]">
                            {invoice.customer_name || t('customers.title')}
                          </p>
                          <p className="text-xs text-slate-500">{formatDate(invoice.invoice_date)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-slate-900">
                          {formatCurrency(invoice.total)}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          invoice.status === "paid" ? "bg-green-100 text-green-700" :
                          invoice.status === "overdue" ? "bg-red-100 text-red-700" :
                          invoice.status === "sent" ? "bg-blue-100 text-blue-700" :
                          "bg-slate-100 text-slate-600"
                        }`}>
                          {invoice.status === "paid" ? t('invoices.status.paid') :
                           invoice.status === "overdue" ? t('invoices.status.overdue') :
                           invoice.status === "sent" ? t('invoices.status.sent') : t('invoices.status.draft')}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Receipt className="mx-auto mb-2 text-slate-300" size={32} />
                  <p className="text-sm">{t('dashboard.noInvoices')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/invoices" className="card-base p-6 hover:border-indigo-300 transition-colors group" data-testid="quick-invoice">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
              <Receipt className="text-indigo-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">{t('dashboard.scanInvoice')}</h3>
              <p className="text-sm text-slate-500">{t('dashboard.uploadAndScan')}</p>
            </div>
          </div>
        </Link>

        <Link to="/bank" className="card-base p-6 hover:border-indigo-300 transition-colors group" data-testid="quick-bank">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center group-hover:bg-green-100 transition-colors">
              <Wallet className="text-green-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">{t('bankImport.importFile')}</h3>
              <p className="text-sm text-slate-500">{t('common.upload')} CSV/Excel</p>
            </div>
          </div>
        </Link>

        <Link to="/assistant" className="card-base p-6 hover:border-indigo-300 transition-colors group" data-testid="quick-ai">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl ai-gradient flex items-center justify-center">
              <span className="text-white text-lg">AI</span>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">{t('nav.aiAssistant')}</h3>
              <p className="text-sm text-slate-500">{t('common.search')}</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
